import {
  ArticleFiltersInput,
  LanguageId,
  PublicationStatus,
} from 'lib-knowledge-management-common/graphql';

export type PublicationStatusKey = keyof typeof PublicationStatus;

export type AppFilterStore = {
  search?: string | null;
  /**
   * Represents a status of the publication. Singular name as this is what will
   * be used in the URL for each publicationStatus selected
   */
  publicationStatus?: PublicationStatusKey[] | null;
  /**
   * Represents an ownder identifier. Singular name as this is what will be used
   * in the URL for each ownerId selected
   */
  ownerId?: string[] | null;
  /**
   * Represents a collection identifier. Singular name as this is what will be
   * used in the URL for each collectionId selected
   */
  collectionId?: string[] | null;
  excludeOrphans?: string | null;
  /**
   * Represents a language identifier. Singular name as this is what will be
   * used in the URL for each langId selected
   */
  langId?: LanguageId[] | null;
};

const noCollectionsId = 'noCollections';

/**
 * Filters out invalid or specific collection IDs from a given array of
 * collection IDs.
 */
export function filterValidCollectionIds({
  collectionId,
  excludeOrphans,
}: {
  collectionId: string[] | null | undefined;
  excludeOrphans: string | null | undefined;
}): string[] | undefined {
  // If collectionId is null or undefined, return undefined
  if (collectionId === null || collectionId === undefined) {
    return undefined;
  }

  // Filter out the noCollectionsId
  const filteredCollectionIds = collectionId.filter(
    (id) => id !== noCollectionsId,
  );

  // Case: Empty array and excludeOrphans is true
  if (filteredCollectionIds.length === 0 && excludeOrphans === 'true') {
    return [];
  }

  // Case: Empty array and excludeOrphans is false or null/undefined
  if (
    filteredCollectionIds.length === 0 &&
    (excludeOrphans === 'false' || excludeOrphans == null)
  ) {
    return undefined;
  }

  // For all other cases, return the filtered array
  return filteredCollectionIds;
}

export function getArticleFilters(
  store: AppFilterStore,
): ArticleFiltersInput | undefined {
  const { collectionId, publicationStatus, ownerId, excludeOrphans, langId } =
    store;
  const filters: Partial<ArticleFiltersInput> = {};

  if (collectionId?.length) {
    filters.collectionId = filterValidCollectionIds({
      collectionId,
      excludeOrphans,
    });
  }
  if (publicationStatus?.length) {
    filters.publicationStatus =
      transformPublicationStatusEnums(publicationStatus);
  }
  if (ownerId?.length) {
    filters.ownerId = ownerId;
  }
  if (excludeOrphans === 'true') {
    filters.excludeOrphans = true;
  } else if (excludeOrphans === 'false') {
    if (Object.keys(filters).length === 0 && collectionId === undefined) {
      // Only excludeOrphans is set to 'false'
      return { excludeOrphans: false, collectionId: [] };
    } else if (
      collectionId === null ||
      (Array.isArray(collectionId) && collectionId.length === 0)
    ) {
      // collectionId is null or an empty array
      return undefined;
    } else {
      filters.excludeOrphans = false;
    }
  }

  if (Array.isArray(langId)) filters.languageId = langId;

  return Object.keys(filters).length ? filters : undefined;
}

/** Maps a URL representation of the Enum value back into an actual enum value */
function transformPublicationStatusEnums(
  publicationStatusFromUrl: string[] | null | undefined,
): PublicationStatus[] | undefined {
  if (!publicationStatusFromUrl || !publicationStatusFromUrl.length)
    return undefined;

  const statuses = publicationStatusFromUrl
    .map((stat) => {
      switch (stat) {
        case 'Published':
        case 'published':
          return PublicationStatus.Published;
        case 'Unpublished':
        case 'unpublished':
          return PublicationStatus.Unpublished;
        case 'NeverPublished':
        case 'neverpublished':
          return PublicationStatus.NeverPublished;
        case 'PublishedWithDraft':
        case 'publishedwithdraft':
          return PublicationStatus.PublishedWithDraft;
        default:
          return undefined; // This should never happen unless a user is messing with the URL
      }
    })
    .filter(Boolean) as PublicationStatus[];
  return statuses;
}
